<template>
  <div class="submissions-view">
    <div class="submission-header">
      <h3>
        {{ $t("message.forms_page.all_submissions") || "All Submissions" }}
      </h3>
      <div class="submission-controls">
        <div class="filter-group">
          <label>{{
            $t("message.forms_page.filter_by_form") || "Filter by Form"
          }}</label>
          <select v-model="selectedFormFilter" class="filter-select">
            <option :value="null">-- All Forms --</option>
            <option
              v-for="form in forms"
              :key="form.id"
              :value="String(form.id)"
            >
              {{ form.name }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <label class="checkbox-label">
            <input type="checkbox" v-model="onlyUnprocessed" />
            {{
              $t("message.forms_page.only_unprocessed") || "Only unprocessed"
            }}
          </label>
        </div>
      </div>
    </div>

    <div v-if="!loading" class="submissions-container">
      <div v-if="filteredSubmissions.length === 0" class="empty-state">
        {{ $t("message.forms_page.no_submissions") }}
      </div>
      <div v-else class="submissions-grid">
        <div
          v-for="submission in filteredSubmissions"
          :key="submission.id"
          class="submission-card"
          :class="{ processed: submission.is_processed == 1 }"
        >
          <div class="submission-summary">
            <div class="submission-header">
              <div class="submission-info">
                <span class="form-name-badge">{{ submission.form_name }}</span>
                <span class="submission-date">
                  {{ formatDate(submission.created_at) }}
                </span>
                <span class="submission-id">ID: {{ submission.id }}</span>
                <span v-if="submission.seller_name" class="seller-badge">
                  {{ submission.seller_name }}
                </span>
              </div>
              <span
                :class="[
                  'status-badge',
                  submission.is_processed == 1 ? 'processed' : 'new'
                ]"
              >
                {{ submission.is_processed == 1 ? "Processed" : "New" }}
              </span>
            </div>
            <div class="summary-fields">
              <div
                v-for="(value, key) in getFirstThreeFields(
                  submission.responses
                )"
                :key="key"
                class="summary-item"
              >
                <strong>{{ key }}:</strong>
                <span class="truncate">{{ formatFieldValue(value) }}</span>
              </div>
            </div>
          </div>

          <!-- Card Actions -->
          <div class="submission-card-actions">
            <button
              class="btn-action-small"
              @click="viewDetails(submission)"
              title="View full details"
            >
              {{ $t("message.forms_page.view_details") || "View Details" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="loading-state">
      <BaseMiniSpinner />
    </div>
  </div>
</template>

<script>
import BaseMiniSpinner from "@/components/global/_base-mini-spinner.vue";

export default {
  name: "AllSubmissionsView",
  components: {
    BaseMiniSpinner
  },
  props: {
    submissions: {
      type: Array,
      required: true
    },
    forms: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedFormFilter: null,
      onlyUnprocessed: false
    };
  },
  computed: {
    filteredSubmissions() {
      let result = this.submissions;
      if (this.selectedFormFilter) {
        result = result.filter(
          s => String(s.form_id) === this.selectedFormFilter
        );
      }
      if (this.onlyUnprocessed) {
        result = result.filter(s => s.is_processed != 1);
      }
      return result;
    }
  },
  methods: {
    viewDetails(submission) {
      this.$router.push({
        name: "FormSubmissionDetailsPage",
        params: { submissionId: submission.id },
        query: { formId: submission.form_id }
      });
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        })
      );
    },
    getFirstThreeFields(responses) {
      const obj = {};
      let count = 0;
      for (const key in responses) {
        if (count >= 3) break;
        obj[key] = responses[key];
        count++;
      }
      return obj;
    },
    formatFieldValue(value) {
      if (Array.isArray(value)) {
        return value.join(", ");
      }
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value);
      }
      return String(value);
    }
  }
};
</script>

<style lang="scss" scoped>
.submissions-view {
  .submission-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e0e0e0;
    flex-wrap: wrap;
    gap: 12px;

    h3 {
      margin: 0;
      font-size: 18px;
      flex: 1;
    }

    .submission-controls {
      display: flex;
      gap: 12px;
      align-items: center;
      flex-wrap: wrap;

      .filter-group {
        display: flex;
        align-items: center;
        gap: 8px;

        label {
          font-size: 13px;
          font-weight: 600;
          color: #666;
          margin: 0;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          font-size: 13px;
          color: #333;
          margin: 0;

          input[type="checkbox"] {
            cursor: pointer;
          }
        }

        .filter-select {
          padding: 6px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 13px;
          cursor: pointer;
          background: white;
          transition: all 0.2s ease;

          &:focus {
            outline: none;
            border-color: #2196f3;
            box-shadow: 0 0 4px rgba(33, 150, 243, 0.3);
          }
        }
      }
    }
  }
}

.submissions-container {
  margin-top: 20px;
}

.submissions-grid {
  display: grid;
  gap: 12px;
}

.submission-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  cursor: pointer;

  &.processed {
    opacity: 0.7;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #2196f3;
  }

  .submission-summary {
    padding: 8px 12px;
    background: #f9f9f9;

    .submission-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 6px;
      font-size: 12px;
      gap: 8px;

      .submission-info {
        display: flex;
        gap: 8px;
        align-items: center;
        flex-wrap: wrap;
      }

      .form-name-badge {
        display: inline-block;
        background: #e3f2fd;
        color: #1976d2;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
      }

      .submission-date {
        color: #666;
        font-weight: 500;
      }

      .submission-id {
        color: #999;
        font-family: monospace;
        font-size: 11px;
      }

      .seller-badge {
        display: inline-block;
        background: #f3e5f5;
        color: #7b1fa2;
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
      }

      .status-badge {
        padding: 2px 8px;
        border-radius: 10px;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;

        &.new {
          background: #c8e6c9;
          color: #1b5e20;
        }

        &.processed {
          background: #e0e0e0;
          color: #666;
        }
      }
    }

    .summary-fields {
      display: flex;
      flex-direction: column;
      gap: 4px;
      font-size: 12px;

      .summary-item {
        display: flex;
        gap: 6px;
        color: #333;

        strong {
          color: #222;
          flex: 0 0 auto;
          font-size: 11px;
        }

        .truncate {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #666;
          font-size: 11px;
        }
      }
    }
  }

  .submission-card-actions {
    padding: 8px 12px;
    background: white;
    border-top: 1px solid #e0e0e0;
    text-align: right;

    .btn-action-small {
      padding: 5px 12px;
      font-size: 12px;
      background: #2196f3;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: #1976d2;
        transform: translateY(-1px);
      }
    }
  }

  @media (max-width: 768px) {
    .submission-summary {
      padding: 8px 10px;

      .submission-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;

        .submission-info {
          flex-direction: column;
          align-items: flex-start;
        }
      }

      .summary-fields {
        font-size: 11px;

        .summary-item strong {
          min-width: 80px;
        }
      }
    }

    .submission-card-actions {
      padding: 8px 10px;

      .btn-action-small {
        font-size: 11px;
      }
    }
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-size: 14px;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 40px;
}
</style>
